import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import {
  ArrowDownLeft,
  ArrowUpRight,
  Minus,
  Plus,
  Printer,
  Scale,
  Ticket,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type {
  CustomerCreditEntrySummary,
  ExchangeDetail,
} from '@aeris/contracts'
import { MONEY_SCALE, formatMoney, fromString, isPositive } from '@aeris/money'
import { formatDocumentNumber, type PaperWidth } from '@aeris/printing'
import { Badge, Button, LoadFailed, Spinner, StatTile, cn } from '@aeris/ui'

import {
  fetchCustomerCredit,
  fetchExchange,
  fetchExchanges,
} from '../lib/api/exchanges.ts'
import { useAuth } from '../lib/auth-context.ts'
import { readRememberedPaper, rememberPaper } from '../lib/remembered-paper.ts'

import { CreditAdjustmentDialog } from './credit-adjustment-dialog.tsx'
import { ExchangeSlipDialog } from './exchange-slip-dialog.tsx'
import { SalesDocumentDialog } from './sales-document-dialog.tsx'

/**
 * O vale do cliente — F126, refeito na F208.
 *
 * É o contrário do limite de crédito: o limite é o quanto o cliente PODE FICAR
 * DEVENDO; o vale é o quanto a LOJA DEVE ao cliente. O dono pediu o histórico
 * inteiro "com tudo": de onde veio cada vale, onde foi usado, e o que sobra.
 * Então: três números no topo, e o extrato linha a linha com o saldo depois de
 * cada uma — a mesma leitura de um extrato de banco — e a venda ou a troca de
 * cada linha a um clique.
 */

const PAGE_SIZE = 20

const dateTime = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
})

const money = (wire: string): string =>
  formatMoney(fromString(wire, MONEY_SCALE))

const numberOf = (value: number | null): string =>
  value === null ? '' : ` nº ${formatDocumentNumber(value)}`

/** Um pedaço clicável do rótulo: abre a venda ou a troca. */
type Target =
  | { readonly kind: 'sale'; readonly id: string }
  | { readonly kind: 'exchange'; readonly id: string }

type Label = {
  readonly text: string
  readonly target: Target | null
  /** "da venda nº 480", quando a troca veio de uma venda. */
  readonly origin: { readonly text: string; readonly saleId: string } | null
}

/** O que foi cada linha, na língua do balcão. */
const entryLabel = (entry: CustomerCreditEntrySummary): Label => {
  const sale = (text: string): Label => ({
    text,
    target:
      entry.sourceId === null ? null : { kind: 'sale', id: entry.sourceId },
    origin: null,
  })

  switch (entry.sourceType) {
    case 'exchange':
      return {
        text: `${entry.exchangeKind === 'exchange' ? 'Troca' : 'Vale'}${numberOf(entry.sourceNumber)}`,
        target:
          entry.sourceId === null
            ? null
            : { kind: 'exchange', id: entry.sourceId },
        origin:
          entry.originSaleId === null
            ? null
            : {
                text: `da venda${numberOf(entry.originSaleNumber)}`,
                saleId: entry.originSaleId,
              },
      }
    case 'sales_document':
      if (entry.kind === 'redeemed') {
        return sale(`Usado na compra${numberOf(entry.sourceNumber)}`)
      }
      if (entry.kind === 'voucher_issued') {
        return sale(`Pedido${numberOf(entry.sourceNumber)} cancelado`)
      }
      return sale(
        `Venda${numberOf(entry.sourceNumber)} cancelada — o vale voltou`
      )
    case 'finance_entry':
      return {
        text:
          entry.entryDescription === null
            ? 'Pagou um título'
            : `Pagou o título: ${entry.entryDescription}`,
        target: null,
        origin: null,
      }
    case 'manual':
      return {
        text:
          entry.kind === 'granted'
            ? 'Vale dado à mão'
            : entry.amount.startsWith('-')
              ? 'Vale tirado à mão'
              : 'Ajuste',
        target: null,
        origin: null,
      }
  }
}

export const PartyVoucherPanel = ({
  partyId,
  customerName = 'Cliente',
}: {
  partyId: string
  customerName?: string
}): ReactNode => {
  const { allows } = useAuth()
  const [adjusting, setAdjusting] = useState<'give' | 'take' | null>(null)
  const [openSale, setOpenSale] = useState<string | null>(null)
  const [printing, setPrinting] = useState<ExchangeDetail | null>(null)
  const [paper, setPaper] = useState<PaperWidth>(readRememberedPaper)

  const statement = useInfiniteQuery({
    // Debaixo de `customer-credit`: quem invalida o saldo invalida o extrato.
    queryKey: ['customer-credit', partyId, 'statement'],
    queryFn: async ({ pageParam, signal }) =>
      await fetchCustomerCredit(partyId, signal, {
        page: pageParam,
        pageSize: PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })

  const openExchange = useMutation({
    // O erro aparece na própria tela; o aviso geral não repete (F248).

    meta: { errorShownInline: true },
    mutationFn: async (id: string) => await fetchExchange(id),
    onSuccess: (response) => {
      setPrinting(response.exchange)
    },
  })

  const head = statement.data?.pages[0]
  const entries = statement.data?.pages.flatMap((page) => page.entries) ?? []

  const open = (target: Target): void => {
    if (target.kind === 'sale') setOpenSale(target.id)
    else openExchange.mutate(target.id)
  }

  return (
    <section aria-label="Vale do cliente" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
          <Ticket aria-hidden="true" className="h-3.5 w-3.5" />
          Vale — o que a loja deve ao cliente
        </h3>
        {/*
          F216: dar e tirar vale na mão. O botão aparece para quem mexe em vale
          (o trocador também); o que decide é a senha do dono ou do admin.
        */}
        {head !== undefined &&
        (allows('exchange.credit.adjust') || allows('exchange.manage')) ? (
          <div className="flex gap-1.5">
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Plus aria-hidden="true" className="h-3.5 w-3.5" />}
              onClick={() => {
                setAdjusting('give')
              }}
            >
              Dar vale
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!isPositive(fromString(head.balance, MONEY_SCALE))}
              leadingIcon={<Minus aria-hidden="true" className="h-3.5 w-3.5" />}
              onClick={() => {
                setAdjusting('take')
              }}
            >
              Tirar vale
            </Button>
          </div>
        ) : null}
      </div>

      {statement.isPending ? (
        <Spinner label="Carregando o vale do cliente" />
      ) : statement.isError || head === undefined ? (
        <LoadFailed
          what="o vale do cliente"
          onRetry={() => {
            void statement.refetch()
          }}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <StatTile
              label="Saldo disponível"
              // F216: de quem é o saldo. O que o cliente usa sai primeiro do
              // antigo (F214).
              hint={`da loja ${money(head.byOrigin.store)} · da gestão antiga ${money(head.byOrigin.old)}`}
              value={
                <span
                  className={cn(
                    'font-(--font-mono) tabular-nums',
                    isPositive(fromString(head.balance, MONEY_SCALE)) &&
                      'text-(--success)'
                  )}
                >
                  {money(head.balance)}
                </span>
              }
            />
            <StatTile
              label="Já recebeu em vale"
              value={
                <span className="font-(--font-mono) tabular-nums">
                  {money(head.totals.received)}
                </span>
              }
            />
            <StatTile
              label="Já usou"
              value={
                <span className="font-(--font-mono) tabular-nums">
                  {money(head.totals.used)}
                </span>
              }
            />
          </div>

          {entries.length === 0 ? (
            <p className="rounded-(--radius-md) border border-dashed border-(--border-subtle) px-3 py-4 text-center text-(length:--text-sm) text-(--text-muted)">
              Este cliente nunca teve vale. O vale nasce numa troca, num pedido
              cancelado ou dado à mão pelo admin.
            </p>
          ) : (
            <ol aria-label="Extrato do vale" className="flex flex-col gap-1.5">
              {entries.map((entry) => (
                <EntryRow key={entry.id} entry={entry} onOpen={open} />
              ))}
            </ol>
          )}

          {entries.length === 0 ? null : (
            <div className="flex flex-wrap items-center justify-between gap-2 text-(length:--text-xs) text-(--text-muted)">
              <span>
                {String(entries.length)} de {String(head.total)} movimentos
              </span>
              {statement.hasNextPage ? (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={statement.isFetchingNextPage}
                  onClick={() => {
                    void statement.fetchNextPage()
                  }}
                >
                  Carregar mais
                </Button>
              ) : null}
            </div>
          )}

          {openExchange.isError ? (
            <p role="alert" className="text-(length:--text-xs) text-(--danger)">
              Não foi possível abrir a troca agora.
            </p>
          ) : null}
        </>
      )}

      <CustomerExchanges
        partyId={partyId}
        opening={openExchange.isPending ? openExchange.variables : null}
        onOpen={(id) => {
          openExchange.mutate(id)
        }}
      />

      {adjusting === null || head === undefined ? null : (
        <CreditAdjustmentDialog
          partyId={partyId}
          customerName={customerName}
          direction={adjusting}
          open={head.byOrigin}
          onClose={() => {
            setAdjusting(null)
          }}
        />
      )}
      {openSale === null ? null : (
        <SalesDocumentDialog
          documentId={openSale}
          onClose={() => {
            setOpenSale(null)
          }}
        />
      )}
      {printing === null ? null : (
        <ExchangeSlipDialog
          exchange={printing}
          paper={paper}
          onPaperChange={(next) => {
            setPaper(next)
            rememberPaper(next)
          }}
          onClose={() => {
            setPrinting(null)
          }}
        />
      )}
    </section>
  )
}

const EntryRow = ({
  entry,
  onOpen,
}: {
  entry: CustomerCreditEntrySummary
  onOpen: (target: Target) => void
}): ReactNode => {
  const label = entryLabel(entry)
  const out = entry.amount.startsWith('-')
  const adjustment =
    entry.kind === 'adjustment' && entry.sourceType === 'manual'
  const Icon = adjustment ? Scale : out ? ArrowUpRight : ArrowDownLeft
  const note =
    entry.note !== null &&
    (entry.sourceType === 'manual' || entry.sourceType === 'finance_entry')
      ? entry.note
      : null

  return (
    <li className="flex items-start gap-3 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface) px-3 py-2">
      <span
        aria-hidden="true"
        className={cn(
          'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
          adjustment
            ? 'bg-(--surface-sunken) text-(--text-secondary)'
            : out
              ? 'bg-(--danger-subtle) text-(--danger)'
              : 'bg-(--success-subtle) text-(--success)'
        )}
      >
        <Icon className="h-4 w-4" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-(length:--text-sm) text-(--text-primary)">
          {label.target === null ? (
            label.text
          ) : (
            <button
              type="button"
              className="text-left font-(--weight-semibold) underline decoration-dotted underline-offset-2 hover:text-(--brand-primary)"
              onClick={() => {
                if (label.target !== null) onOpen(label.target)
              }}
            >
              {label.text}
            </button>
          )}
          {label.origin === null ? null : (
            <>
              {' '}
              <button
                type="button"
                className="text-(--text-secondary) underline decoration-dotted underline-offset-2 hover:text-(--brand-primary)"
                onClick={() => {
                  if (label.origin !== null) {
                    onOpen({ kind: 'sale', id: label.origin.saleId })
                  }
                }}
              >
                {label.origin.text}
              </button>
            </>
          )}
        </p>
        <p className="text-(length:--text-xs) text-(--text-muted)">
          {dateTime.format(new Date(entry.occurredAt))}
          {entry.branchName === null ? '' : ` · ${entry.branchName}`}
          {entry.actorName === null ? '' : ` · por ${entry.actorName}`}
        </p>
        {entry.oldManagement === true ? (
          <span>
            <Badge tone="warning">gestão antiga</Badge>
          </span>
        ) : null}
        {note === null ? null : (
          <p className="text-(length:--text-xs) text-(--text-secondary)">
            {note}
          </p>
        )}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <span
          className={cn(
            'font-(--font-mono) text-(length:--text-sm) font-(--weight-semibold) tabular-nums',
            out ? 'text-(--danger)' : 'text-(--success)'
          )}
        >
          {out ? '−' : '+'}
          {money(entry.amount.replace('-', ''))}
        </span>
        <span className="font-(--font-mono) text-(length:--text-xs) text-(--text-muted) tabular-nums">
          saldo {money(entry.balanceAfter)}
        </span>
      </div>
    </li>
  )
}

/**
 * As trocas deste cliente, com o comprovante — a mesma lista de `/trocas`,
 * filtrada por quem trocou. O filtro `customerPartyId` já existia na API.
 */
const CustomerExchanges = ({
  partyId,
  opening,
  onOpen,
}: {
  partyId: string
  opening: string | null | undefined
  onOpen: (id: string) => void
}): ReactNode => {
  const { allows } = useAuth()
  const exchanges = useQuery({
    queryKey: ['exchanges', 'customer', partyId],
    queryFn: async ({ signal }) =>
      await fetchExchanges(
        { customerPartyId: partyId, status: 'completed', pageSize: 10 },
        signal
      ),
    enabled: allows('exchange.read'),
  })

  const items = exchanges.data?.items ?? []
  if (items.length === 0) return null

  return (
    <div className="flex flex-col gap-1.5">
      <h4 className="text-(length:--text-xs) font-(--weight-semibold) text-(--text-secondary) uppercase">
        Trocas deste cliente
        {exchanges.data !== undefined && exchanges.data.total > items.length
          ? ` (últimas ${String(items.length)} de ${String(exchanges.data.total)})`
          : ''}
      </h4>
      <ul className="flex flex-col gap-1">
        {items.map((exchange) => (
          <li
            key={exchange.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-(--radius-md) border border-(--border-subtle) px-3 py-2 text-(length:--text-xs)"
          >
            <span className="text-(--text-secondary)">
              <strong className="text-(--text-primary)">
                {exchange.kind === 'exchange' ? 'Troca' : 'Vale'}
                {numberOf(exchange.number)}
              </strong>
              {exchange.originalSaleId === null ? ' · gestão antiga' : ''}
              {exchange.completedAt === null
                ? ''
                : ` · ${dateTime.format(new Date(exchange.completedAt))}`}
              {` · devolveu ${money(exchange.returnedTotal)}`}
              {exchange.voucherCreditAmount === '0.00'
                ? ''
                : ` · vale ${money(exchange.voucherCreditAmount)}`}
              {exchange.handledByName === null
                ? ''
                : ` · ${exchange.handledByName}`}
            </span>
            <Button
              variant="ghost"
              size="sm"
              loading={opening === exchange.id}
              leadingIcon={
                <Printer aria-hidden="true" className="h-3.5 w-3.5" />
              }
              onClick={() => {
                onOpen(exchange.id)
              }}
            >
              Comprovante
            </Button>
          </li>
        ))}
      </ul>
    </div>
  )
}
